// SPDX-License-Identifier: AGPL-3.0-only

import { buildSourceRateGrid, gridMetrics } from './grid';
import { AdaptiveSimulationDomain, type SimulationDomainWindow } from './AdaptiveSimulationDomain';
import {
  DEFAULT_SOLVER_PARAMETERS,
  type DemGrid,
  type InflowSource,
  type SimulationSnapshot,
  type SimulationStatistics,
  type SolverParameters,
  validateDemGrid
} from './types';

const EAST = 0;
const NORTH = 1;
const WEST = 2;
const SOUTH = 3;

export class CpuShallowWaterSolver {
  readonly grid: DemGrid;
  readonly parameters: SolverParameters;
  readonly depth: Float32Array;
  readonly velocityEast: Float32Array;
  readonly velocityNorth: Float32Array;

  private readonly flux: Float32Array;
  private readonly domain: AdaptiveSimulationDomain;
  private sourceRate: Float32Array;
  private elapsedSeconds = 0;
  private iteration = 0;

  constructor(grid: DemGrid, parameters: Partial<SolverParameters> = {}) {
    validateDemGrid(grid);
    this.grid = grid;
    this.parameters = { ...DEFAULT_SOLVER_PARAMETERS, ...parameters };
    this.depth = new Float32Array(grid.width * grid.height);
    this.velocityEast = new Float32Array(this.depth.length);
    this.velocityNorth = new Float32Array(this.depth.length);
    this.flux = new Float32Array(this.depth.length * 4);
    this.sourceRate = new Float32Array(this.depth.length);
    this.domain = new AdaptiveSimulationDomain(grid.width, grid.height, {
      enabled: this.parameters.adaptiveDomainEnabled,
      tileSizeCells: this.parameters.adaptiveDomainTileSizeCells,
      initialPaddingCells: this.parameters.adaptiveDomainInitialPaddingCells,
      edgeMarginCells: this.parameters.adaptiveDomainEdgeMarginCells,
      wetDepthThresholdMeters: this.parameters.minimumWetDepthMeters
    });
  }

  get activeDomain(): SimulationDomainWindow {
    return this.domain.window;
  }

  setSources(sources: readonly InflowSource[]): void {
    this.sourceRate = buildSourceRateGrid(this.grid, sources);
    const hasSource = this.sourceRate.some((value) => value > 0);
    if (hasSource && this.iteration === 0 && !this.depth.some((value) => value > 0)) {
      this.domain.resetToMask(this.sourceRate);
    } else if (hasSource) {
      this.domain.includeMask(this.sourceRate);
    }
  }

  reset(): void {
    this.depth.fill(0);
    this.velocityEast.fill(0);
    this.velocityNorth.fill(0);
    this.flux.fill(0);
    this.elapsedSeconds = 0;
    this.iteration = 0;
    this.domain.resetToMask(this.sourceRate);
  }

  step(requestedTimeStepSeconds?: number): number {
    const dt = requestedTimeStepSeconds ?? this.stableTimeStep();
    if (!(dt > 0) || !Number.isFinite(dt))
      throw new Error('Time step must be finite and positive.');
    this.domain.expandForDepth(this.depth);
    this.computeOutflow(dt);
    this.updateDepth(dt);
    this.domain.expandForDepth(this.depth);
    this.elapsedSeconds += dt;
    this.iteration += 1;
    return dt;
  }

  stableTimeStep(): number {
    const metrics = gridMetrics(this.grid);
    const cellSize = 0.5 * (metrics.cellWidthMeters + metrics.cellHeightMeters);
    return Math.max(
      0.005,
      Math.min(this.parameters.maxTimeStepSeconds, 0.015 * Math.sqrt(Math.max(cellSize, 0.1) / 5))
    );
  }

  snapshot(): SimulationSnapshot {
    return {
      depth: this.depth.slice(),
      velocityEast: this.velocityEast.slice(),
      velocityNorth: this.velocityNorth.slice(),
      statistics: this.statistics()
    };
  }

  statistics(): SimulationStatistics {
    const area = gridMetrics(this.grid).cellAreaSquareMeters;
    let wetCellCount = 0;
    let maximumDepthMeters = 0;
    let wetDepthSum = 0;
    let waterVolumeCubicMeters = 0;
    for (const depth of this.depth) {
      maximumDepthMeters = Math.max(maximumDepthMeters, depth);
      waterVolumeCubicMeters += depth * area;
      if (depth >= this.parameters.minimumWetDepthMeters) {
        wetCellCount += 1;
        wetDepthSum += depth;
      }
    }
    return {
      elapsedSeconds: this.elapsedSeconds,
      iteration: this.iteration,
      wetCellCount,
      maximumDepthMeters,
      meanWetDepthMeters: wetCellCount === 0 ? 0 : wetDepthSum / wetCellCount,
      waterVolumeCubicMeters
    };
  }

  private computeOutflow(dt: number): void {
    const { width, height, elevations } = this.grid;
    const metrics = gridMetrics(this.grid);
    const area = metrics.cellAreaSquareMeters;
    const pipeArea = Math.max(
      0.08,
      this.parameters.pipeAreaScale * Math.min(metrics.cellWidthMeters, metrics.cellHeightMeters)
    );
    const domain = this.domain.window;
    const domainMaxX = domain.x + domain.width - 1;
    const domainMaxY = domain.y + domain.height - 1;

    for (let y = domain.y; y <= domainMaxY; y += 1) {
      for (let x = domain.x; x <= domainMaxX; x += 1) {
        const index = y * width + x;
        const depth = this.depth[index] ?? 0;
        const head = (elevations[index] ?? 0) + depth;
        const fluxOffset = index * 4;
        const neighbours = [
          x + 1 < width ? index + 1 : -1,
          y > 0 ? index - width : -1,
          x > 0 ? index - 1 : -1,
          y + 1 < height ? index + width : -1
        ];
        const distances = [
          metrics.cellWidthMeters,
          metrics.cellHeightMeters,
          metrics.cellWidthMeters,
          metrics.cellHeightMeters
        ];
        const nextFluxes = [0, 0, 0, 0];
        let totalOutflow = 0;

        for (let direction = 0; direction < 4; direction += 1) {
          const neighbourIndex = neighbours[direction] ?? -1;
          const neighbourInsideDomain =
            direction === EAST
              ? x < domainMaxX
              : direction === NORTH
                ? y > domain.y
                : direction === WEST
                  ? x > domain.x
                  : y < domainMaxY;
          let nextFlux = 0;
          if (neighbourIndex >= 0 && neighbourInsideDomain && depth > 0) {
            const neighbourDepth = this.depth[neighbourIndex] ?? 0;
            const neighbourHead = (elevations[neighbourIndex] ?? 0) + neighbourDepth;
            const hydraulicHeadDrop = Math.max(0, head - neighbourHead);
            const averageDepth = Math.max(0, (depth + neighbourDepth) * 0.5);
            const flowArea = pipeArea * averageDepth;
            nextFlux =
              this.parameters.fluxDamping * (this.flux[fluxOffset + direction] ?? 0) +
              (dt * flowArea * this.parameters.gravity * hydraulicHeadDrop) /
                Math.max(distances[direction] ?? 1, 0.001);
          }
          nextFluxes[direction] = Math.max(0, nextFlux);
          totalOutflow += Math.max(0, nextFlux);
        }

        const wetDepth = Math.max(depth, this.parameters.minimumWetDepthMeters);
        const speed = totalOutflow / Math.max(pipeArea * wetDepth, 0.0001);
        const frictionDecay =
          1 /
          (1 +
            (dt * this.parameters.gravity * this.parameters.roughness ** 2 * speed) /
              wetDepth ** (4 / 3));
        totalOutflow *= frictionDecay;
        for (let direction = 0; direction < 4; direction += 1) {
          this.flux[fluxOffset + direction] = (nextFluxes[direction] ?? 0) * frictionDecay;
        }

        const maximumOutflow = (depth * area) / dt;
        if (totalOutflow > maximumOutflow && totalOutflow > 0) {
          const scale = maximumOutflow / totalOutflow;
          for (let direction = 0; direction < 4; direction += 1) {
            this.flux[fluxOffset + direction] = (this.flux[fluxOffset + direction] ?? 0) * scale;
          }
        }
      }
    }
  }

  private updateDepth(dt: number): void {
    const { width, height } = this.grid;
    const metrics = gridMetrics(this.grid);
    const area = metrics.cellAreaSquareMeters;
    const nextDepth = this.depth.slice();
    const domain = this.domain.window;
    const domainMaxX = domain.x + domain.width - 1;
    const domainMaxY = domain.y + domain.height - 1;

    for (let y = domain.y; y <= domainMaxY; y += 1) {
      for (let x = domain.x; x <= domainMaxX; x += 1) {
        const index = y * width + x;
        const ownFluxOffset = index * 4;
        const outflow =
          (this.flux[ownFluxOffset + EAST] ?? 0) +
          (this.flux[ownFluxOffset + NORTH] ?? 0) +
          (this.flux[ownFluxOffset + WEST] ?? 0) +
          (this.flux[ownFluxOffset + SOUTH] ?? 0);
        const inflowEast = x < domainMaxX ? (this.flux[(index + 1) * 4 + WEST] ?? 0) : 0;
        const inflowNorth = y > domain.y ? (this.flux[(index - width) * 4 + SOUTH] ?? 0) : 0;
        const inflowWest = x > domain.x ? (this.flux[(index - 1) * 4 + EAST] ?? 0) : 0;
        const inflowSouth = y < domainMaxY ? (this.flux[(index + width) * 4 + NORTH] ?? 0) : 0;
        const inflow = inflowEast + inflowNorth + inflowWest + inflowSouth;
        const sourceDepth = (this.sourceRate[index] ?? 0) * dt;
        let depth = Math.min(
          this.parameters.maximumDepthMeters,
          Math.max(0, (this.depth[index] ?? 0) + ((inflow - outflow) * dt) / area + sourceDepth)
        );
        if (
          this.parameters.openBoundaryDrainRate > 0 &&
          (x === 0 || x === width - 1 || y === 0 || y === height - 1)
        ) {
          depth *= Math.exp(-this.parameters.openBoundaryDrainRate * dt);
        }
        nextDepth[index] = depth;
        const averageDepth = 0.5 * ((this.depth[index] ?? 0) + depth);
        const westEast = x > domain.x ? (this.flux[(index - 1) * 4 + EAST] ?? 0) : 0;
        const eastWest = x < domainMaxX ? (this.flux[(index + 1) * 4 + WEST] ?? 0) : 0;
        const northSouth = y > domain.y ? (this.flux[(index - width) * 4 + SOUTH] ?? 0) : 0;
        const southNorth = y < domainMaxY ? (this.flux[(index + width) * 4 + NORTH] ?? 0) : 0;
        this.velocityEast[index] =
          averageDepth > 0.01
            ? clamp(
                ((westEast -
                  (this.flux[ownFluxOffset + WEST] ?? 0) +
                  ((this.flux[ownFluxOffset + EAST] ?? 0) - eastWest)) *
                  0.5) /
                  Math.max(metricsFaceArea(averageDepth, metrics.cellHeightMeters), 0.001),
                -30,
                30
              )
            : 0;
        this.velocityNorth[index] =
          averageDepth > 0.01
            ? clamp(
                ((northSouth -
                  (this.flux[ownFluxOffset + SOUTH] ?? 0) +
                  ((this.flux[ownFluxOffset + NORTH] ?? 0) - southNorth)) *
                  0.5) /
                  Math.max(metricsFaceArea(averageDepth, metrics.cellWidthMeters), 0.001),
                -30,
                30
              )
            : 0;
      }
    }
    this.depth.set(nextDepth);
  }
}

function metricsFaceArea(depth: number, faceWidth: number): number {
  return depth * faceWidth;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
